import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select as MuiSelect,
} from '@material-ui/core';
import {
  Button,
  Flex,
  IconButton,
  Select,
  TextField,
  Typography,
} from '@knockrentals/knock-shared-web-v2';
import {
  FeatureDialogActions,
  makeCss,
} from '../../../../../knock-design-system';
import { KnockEmail } from '../KnockEmail';
import { LogoActionTile } from './LogoActionTile';
import { usePropertyCreation } from './use-property-creation';
import { stateOptions } from './constants';

export const CREATE_PRIMARY_BUTTON_TEXT = 'Create Property';

const useCss = makeCss({
  divider: {
    margin: '20px 0px',
  },
  helperText: {
    marginLeft: '14px',
    marginRight: '14px',
  },
  flexGrowContainer: {
    display: 'flex',
    gap: '16px',

    '& .MuiFormControl-root': {
      flex: 1,
    },
  },
});

const CreateProperty = ({ closeDialog, createProperty }) => {
  const {
    clearErrorMessage,
    errorMessages,
    handleBlur,
    handleChange,
    handleEmailChange,
    handleSubmit,
    knockEmailStatus,
    leasingTeams,
    isNewAddressLineVisible,
    isSaveButtonDisabled,
    newPropertyData,
    toggleAdditionalAddressLine,
    updateLogos,
    validateEmailUsername,
  } = usePropertyCreation({ createProperty });
  const css = useCss();

  return (
    <React.Fragment>
      <DialogContent dividers>
        <Typography color="secondary.700" variant="body2">
          First, let’s cover the basics. Once created, you can add more detailed
          info about your property.
        </Typography>

        <Flex flexDirection="column" marginTop="24px" gap={24}>
          <TextField
            error={!!errorMessages.propertyName}
            fullWidth
            helperText={errorMessages.propertyName}
            id="propertyName"
            label="Property Name"
            name="propertyName"
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={clearErrorMessage}
            value={newPropertyData.propertyName || ''}
            variant="outlined"
          />

          <TextField
            error={!!errorMessages.propertyAddress}
            fullWidth
            helperText={errorMessages.propertyAddress}
            id="propertyAddress"
            label="Street Address"
            name="propertyAddress"
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={clearErrorMessage}
            value={newPropertyData.propertyAddress || ''}
            variant="outlined"
          />
        </Flex>

        <Box margin="16px 0px">
          {isNewAddressLineVisible ? (
            <TextField
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Remove Address Line 2"
                    color="destructive"
                    iconName="trash-bin"
                    onClick={toggleAdditionalAddressLine}
                    tooltipText="Remove"
                  />
                </InputAdornment>
              }
              fullWidth
              id="propertyAddressLine2"
              label="Address Line 2 (Optional)"
              name="propertyAddressLine2"
              onBlur={handleBlur}
              onChange={handleChange}
              onFocus={clearErrorMessage}
              placeholder="Suite, unit, building, floor, etc."
              value={newPropertyData.propertyAddressLine2 || ''}
              variant="outlined"
            />
          ) : (
            <Button
              onClick={toggleAdditionalAddressLine}
              startIcon="add-md"
              size="small"
              variant="text"
            >
              Add address line (optional)
            </Button>
          )}
        </Box>

        <Flex flexDirection="column" gap={16}>
          <TextField
            error={!!errorMessages.propertyCity}
            helperText={errorMessages.propertyCity}
            id="propertyCity"
            label="City"
            name="propertyCity"
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={clearErrorMessage}
            value={newPropertyData.propertyCity || ''}
            variant="outlined"
          />

          <div css={css.flexGrowContainer}>
            <FormControl error={!!errorMessages.propertyState}>
              <Select
                aria-label="Property State"
                error={!!errorMessages.propertyState}
                id="propertyState"
                label="State"
                name="propertyState"
                onChange={handleChange}
                onFocus={(e) => clearErrorMessage(e, 'propertyState')}
                options={stateOptions}
                value={newPropertyData.propertyState || ''}
                variant="outlined"
              />
              <FormHelperText css={css.helperText}>
                {errorMessages.propertyState}
              </FormHelperText>
            </FormControl>

            <TextField
              error={!!errorMessages.propertyZip}
              helperText={errorMessages.propertyZip}
              id="propertyZip"
              label="Zipcode"
              name="propertyZip"
              onChange={handleChange}
              onFocus={clearErrorMessage}
              variant="outlined"
            />
          </div>
        </Flex>

        <Divider css={css.divider} />

        <Typography variant="subtitle2">Contact Info & More</Typography>

        <Flex alignItems="center" gap={24}>
          <Flex flex={1} flexDirection="column" gap={16} marginTop="20px">
            <FormControl
              error={!!errorMessages.propertyOwner}
              variant="outlined"
            >
              <InputLabel>Owner</InputLabel>
              <MuiSelect
                aria-label="owner"
                error={!!errorMessages.propertyOwner}
                fullWidth
                id="propertyOwner"
                name="propertyOwner"
                onChange={(e) => handleChange(e.target.value, e)}
                onFocus={(e) => clearErrorMessage(e, 'propertyOwner')}
                value={newPropertyData.propertyOwner || ''}
              >
                {leasingTeams.map((team) => [
                  <ListSubheader key={team.leasing_team_id}>
                    {team.leasing_team_name}
                  </ListSubheader>,
                  ...team.users.map((user) => (
                    <MenuItem key={user.user_id} value={String(user.user_id)}>
                      {user.first_name} {user.last_name} ({user.email})
                    </MenuItem>
                  )),
                ])}
              </MuiSelect>
              <FormHelperText css={css.helperText}>
                {errorMessages.propertyOwner}
              </FormHelperText>
            </FormControl>

            <div css={css.flexGrowContainer}>
              <KnockEmail
                clearErrorMessage={clearErrorMessage}
                errorMessage={errorMessages.knockEmail}
                knockEmail={newPropertyData.knockEmail || ''}
                knockEmailStatus={knockEmailStatus}
                handleOnChange={handleEmailChange}
                validateEmailUsername={validateEmailUsername}
              />
            </div>

            <TextField
              error={!!errorMessages.propertyWebsite}
              fullWidth
              helperText={errorMessages.propertyWebsite}
              id="propertyWebsite"
              label="Property Website (optional)"
              name="propertyWebsite"
              onChange={handleChange}
              onFocus={clearErrorMessage}
              placeholder="https://"
              variant="outlined"
            />
          </Flex>

          <Flex flexDirection="column">
            <LogoActionTile
              logos={newPropertyData.logos}
              updateLogos={updateLogos}
            />
            <Typography variant="helperText" color="secondary.500">
              * optional
            </Typography>
          </Flex>
        </Flex>
      </DialogContent>

      <FeatureDialogActions
        isPrimaryButtonDisabled={isSaveButtonDisabled}
        primaryButtonOnClick={handleSubmit}
        primaryButtonText={CREATE_PRIMARY_BUTTON_TEXT}
        tertiaryButtonOnClick={closeDialog}
      />
    </React.Fragment>
  );
};

CreateProperty.propTypes = {
  createProperty: PropTypes.func,
  closeDialog: PropTypes.func,
};

export default CreateProperty;
